import { HttpMethod } from "../constants/http";
import { endpoints } from "../config/endpoints";
import { GenerateTextStreamRequest } from "../models/generateText.models";
import { ThinkingStep, WebSource } from "../models/conversation.models";
import { AttachmentRef } from "../models/attachment.models";
import { fetchWithRetry } from "../utils/apiUtils";

/** Vendor failure relayed mid-stream by the proxy ({"t":"err"} chunk, or a {"t":"done","status":"error"} terminal frame). */
export class StreamGenerationError extends Error {}

/**
 * The stream ended without ever receiving a {"t":"done"} terminal frame — the
 * connection was cut before the server could say whether generation finished
 * or failed. Treated more leniently than StreamGenerationError: the caller
 * should try to recover the persisted message and only show an error if that
 * also comes up empty, since this is the normal shape of e.g. a backgrounded
 * mobile tab where the server-side generation keeps running to completion.
 */
export class StreamTruncatedError extends Error {}

export interface StreamMetadata {
    conversationId: string | null;
    messageId: string | null;
    responseMessageId: string | null;
    aiVendor: string | null;
    /**
     * Instruction the server actually applied to this turn — the explicit
     * selection, or the customer's default when the request sent none.
     */
    instructionId?: string | null;
    inputTokens: number | null;
    outputTokens: number | null;
    totalTokens: number | null;
    tokensReadFromCache: number | null;
    conversationTitle?: string | null;
}

/** Human-in-the-loop request surfaced by a durable run ({"t":"ai"} frame). */
export interface AwaitingInput {
    type?: string;
    node?: string;
    prompt?: string;
    [k: string]: unknown;
}

/** Handlers for the NDJSON chat wire protocol, shared by the live and reattach streams. */
export interface StreamCallbacks {
    onDelta: (chunk: string) => void;
    /**
     * The generation round that just streamed ended in tool calls executed
     * in-graph — its streamed text was a tool-call preamble ("Let me look
     * up…"), not the answer, and a fresh round follows. Discard the text
     * accumulated so far; the preamble stays visible in the thinking flow as
     * the generator step's `reasoning`.
     */
    onContentReset?: () => void;
    /**
     * The vendor's raw finish reason for the turn (end_turn / stop / max_tokens
     * / refusal / content_filter / …). Streamed once, when generation ends —
     * lets the UI always show why it stopped, and a refusal (empty text) isn't
     * a silent blank.
     */
    onStopReason?: (reason: string) => void;
    onThinking?: (step: string) => void;
    onThinkingStep?: (step: ThinkingStep) => void;
    onThinkingDuration?: (millis: number) => void;
    onWebSources?: (sources: WebSource[]) => void;
    onGeneratedAttachments?: (attachments: AttachmentRef[]) => void;
    /** A durable run paused for human/client input — render a prompt + reply box. */
    onAwaitingInput?: (payload: AwaitingInput) => void;
}

/**
 * Reads an NDJSON chat stream body ({"t":"c"|"k"|"ks"|"kd"|"ws"|"ga"|"ai"|"err"})
 * and dispatches each frame to [cb]. Shared by the live generate-text-stream and
 * the durable-run reattach stream so the frame vocabulary lives in one place.
 */
export async function readNdjsonStream(response: Response, cb: StreamCallbacks): Promise<void> {
    const reader = response.body?.getReader();
    if (!reader) {
        throw new Error("Response body is not readable");
    }

    const decoder = new TextDecoder("utf-8");
    let lineBuffer = "";
    let doneFrameReceived = false;

    const processLine = (line: string) => {
        const trimmed = line.trim();
        if (!trimmed) return;
        try {
            const msg = JSON.parse(trimmed);
            if (msg.t === "c") cb.onDelta(msg.v);
            else if (msg.t === "cr") cb.onContentReset?.();
            else if (msg.t === "sr") {
                if (typeof msg.v === "string" && msg.v) cb.onStopReason?.(msg.v);
            }
            else if (msg.t === "k") cb.onThinking?.(msg.v);
            else if (msg.t === "ks") {
                // Structured per-node thinking step from the agent graph
                const step = msg.v;
                if (step && typeof step === "object" && typeof step.node === "string") {
                    cb.onThinkingStep?.(step as ThinkingStep);
                }
            }
            else if (msg.t === "kd") {
                const millis = Number(msg.v);
                if (Number.isFinite(millis)) cb.onThinkingDuration?.(millis);
            }
            else if (msg.t === "ws") {
                if (Array.isArray(msg.v) && msg.v.length > 0) cb.onWebSources?.(msg.v);
            }
            else if (msg.t === "ga") {
                // Files built-in tools produced (e.g. generated PDFs). The
                // proxy relays the retrieval-server's snake_case artifact
                // shape verbatim — normalize to the AttachmentRef the rest of
                // the app uses.
                if (Array.isArray(msg.v) && msg.v.length > 0) {
                    const refs: AttachmentRef[] = msg.v
                        .filter((a: any) => a && typeof a === "object")
                        .map((a: any) => ({
                            attachmentId: a.artifact_id ?? a.attachmentId ?? "",
                            s3Key: a.key ?? a.s3Key ?? "",
                            mediaType: a.media_type ?? a.mediaType ?? "application/octet-stream",
                            filename: a.filename ?? undefined,
                            sizeBytes: typeof a.size_bytes === "number" ? a.size_bytes : undefined,
                        }))
                        .filter((r: AttachmentRef) => r.attachmentId);
                    if (refs.length > 0) cb.onGeneratedAttachments?.(refs);
                }
            }
            else if (msg.t === "ai") {
                // A durable run paused on an in-graph interrupt() and needs input.
                if (msg.v && typeof msg.v === "object") cb.onAwaitingInput?.(msg.v as AwaitingInput);
            }
            else if (msg.t === "err") {
                // Vendor failure mid-stream (quota exceeded, key rejected, ...)
                // — the proxy relays the concise reason; surface it so the
                // response card shows what actually happened.
                throw new StreamGenerationError(
                    typeof msg.v === "string" && msg.v ? msg.v : "Generation failed"
                );
            }
            else if (msg.t === "done") {
                // Terminal frame: the server's definitive word on how the
                // stream ended. "ok"/"cancelled" need no action here — the
                // loop below only cares that status was "error".
                doneFrameReceived = true;
                if (msg.status === "error") {
                    throw new StreamGenerationError(
                        typeof msg.m === "string" && msg.m ? msg.m : "Generation failed"
                    );
                }
            }
            // other types (heartbeat, etc.) are silently ignored
        } catch (e) {
            if (e instanceof StreamGenerationError) throw e;
            // Not JSON. The proxy emits "[Error: <reason>]" as a raw line when
            // generation fails mid-stream — surface it as an error instead of
            // appending it to the generated text.
            const errorMatch = trimmed.match(/^\[Error: ([\s\S]*)\]$/);
            if (errorMatch) {
                throw new StreamGenerationError(errorMatch[1] || 'Stream failed');
            }
            // Other raw content (legacy format) still flows through as text
            cb.onDelta(line);
        }
    };

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            lineBuffer += decoder.decode(value, { stream: true });
            const lines = lineBuffer.split("\n");
            lineBuffer = lines.pop() ?? "";
            for (const line of lines) processLine(line);
        }
        // Flush any remaining partial line
        if (lineBuffer) processLine(lineBuffer);
        if (!doneFrameReceived) {
            // The connection closed before the server's terminal frame
            // arrived — we can't tell "finished" from "cut off mid-response".
            // Callers should attempt recovery before treating this as fatal.
            throw new StreamTruncatedError(
                "Connection to the server was lost before the response finished."
            );
        }
    } catch (error) {
        console.error("Error reading stream", error);
        throw error;
    } finally {
        reader.releaseLock();
    }
}

export async function generateTextStream(
    token: string,
    request: GenerateTextStreamRequest,
    onDelta: (chunk: string) => void,
    onMetadata?: (metadata: StreamMetadata) => void,
    onThinking?: (step: string) => void,
    signal?: AbortSignal,
    onThinkingDuration?: (millis: number) => void,
    onWebSources?: (sources: WebSource[]) => void,
    onThinkingStep?: (step: ThinkingStep) => void,
    onGeneratedAttachments?: (attachments: AttachmentRef[]) => void,
    onAwaitingInput?: (payload: AwaitingInput) => void,
    onContentReset?: () => void,
    onStopReason?: (reason: string) => void
): Promise<void> {
    const response = await fetchWithRetry(endpoints.generateTextStream, {
        method: HttpMethod.POST,
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(request),
        signal,
    });

    if (response.status === 401 || response.status === 403) {
        const err: any = new Error('Session expired')
        err.error = 'login_required'
        throw err
    }

    if (!response.ok) {
        // The proxy returns {"error": "<reason>"} on failures — surface the
        // real reason instead of a generic status line when it's available
        let reason = '';
        try {
            const body = await response.text();
            try {
                reason = JSON.parse(body)?.error ?? '';
            } catch {
                reason = body;
            }
        } catch {
            // Body unreadable — fall back to status text only
        }
        reason = (reason || '').trim();
        throw new Error(
            reason
                ? `${reason} (HTTP ${response.status})`
                : `Text generation failed: ${response.status} ${response.statusText}`
        );
    }

    const conversationId = response.headers.get("X-Conversation-Id");
    const messageId = response.headers.get("X-Message-Id");
    const responseMessageId = response.headers.get("X-Response-Message-Id");
    const aiVendor = response.headers.get("X-Ai-Vendor");
    const instructionId = response.headers.get("X-Instruction-Id");

    // Call onMetadata immediately with header-based fields (tokens arrive later via sentinel)
    if (onMetadata) {
        onMetadata({
            conversationId,
            messageId,
            responseMessageId,
            aiVendor,
            instructionId,
            inputTokens: null,
            outputTokens: null,
            totalTokens: null,
            tokensReadFromCache: null,
        });
    }

    await readNdjsonStream(response, {
        onDelta,
        onContentReset,
        onStopReason,
        onThinking,
        onThinkingStep,
        onThinkingDuration,
        onWebSources,
        onGeneratedAttachments,
        onAwaitingInput,
    });
}
